/**
 * Text field <uo-text-field>
 *
 * Attributes:
 * label
 * helper
 * required
 * value
 * placeholder
 * id
 * disabled
 */

// Import utilities
import { debounce } from '@automator/utilities';

// Import base class and style
import InputBase from '../base';

// Import style for web component
const styles = require( './web-component-style.scss' );

/**
 * Creates <uo-text-field>
 */
class AutomatorInputText extends InputBase( HTMLElement ) {
	/**
	 * Returns the component attributes
	 * We don't use observedAttributes here because we want to add data to the attributes
	 *
	 * @return {Object} The attributes
	 */
	static get componentAttributes() {
		// Extend base observed attributes
		return Object.assign( this.baseComponentAttributes, {
			value: {
				type: 'string',
			},
			placeholder: {
				type: 'string',
			},
			'auto-focus': {
				type: 'boolean',
				propertyKey: 'autoFocus'
			},
			'copy-to-clipboard': {
				type: 'boolean',
				propertyKey: 'copyToClipboard'
			}
		} );
	}

	/**
	 * Creates the component
	 *
	 * @return {undefined}
	 */
	constructor() {
		// Invoke constructor of parent class
		super();

		// Add styles
		this.#addStyles();

		// Create control
		this.#createControl();
	}

	/**
	 * Callback invoked when the component is added to the DOM
	 *
	 * @return {undefined}
	 */
	connectedCallback() {
		// Listen changes to the input value
		this.#listenInputChanges();

		// Autofocus
		this.maybeAutofocusField();

		// Listen copy to clipboard
		this.handleCopyToClipboard();
	}

	/**
	 * Autofocus the field when it's added to the DOM
	 * 
	 * @return {undefined}
	 */
	maybeAutofocusField() {
		// Check if we should do it first
		if ( ! this.autoFocus ) {
			return;
		}

		this.$input.focus();
	}

	/**
	 * Add styles specific to this input
	 *
	 * @return {undefined}
	 */
	#addStyles() {
		// Insert CSS
		this.shadowRoot.querySelector( 'style' ).insertAdjacentHTML( 'beforeEnd', styles );
	}

	/**
	 * Creates the control
	 *
	 * @return {undefined}
	 */
	#createControl() {
		// The control wrapper
		this.$controlWrapper.insertAdjacentHTML(
			'beforeEnd',
			`
            <label class="input-wrapper">
				<input type="text">
				${ this.copyToClipboard ? `
					<uo-tooltip>
						${ UncannyAutomatorBackend.i18n.copyToClipboard }

						<uo-button 
							color="primary"
							size="small"
							slot="target"
							class="input-copy-clipboard"
						>
							<uo-icon id="copy"></uo-icon>
						</uo-button>
					</uo-tooltip>
				` : `` }
			</label>
        `
		);
	}

	/**
	 * Handles the copy to clipboard functionality
	 */
	 handleCopyToClipboard() {
		if ( ! this.copyToClipboard || ! this.$copyToClipboardButton ) {
			return;
		}

		this.$copyToClipboardButton.addEventListener( 'click', () => {
			if (
				! window.isSecureContext ||
				! navigator.clipboard
			) {
				// Check if the field is disabled
				const isDisabled = this.disabled;

				if ( isDisabled ) {
					this.disabled = false;
				}

				// Select the content
				this.$input.select();

				// Disable if again
				if ( isDisabled ) {
					this.disabled = true;
				}

				return;
			}

			// Copy value to the clipboard
			navigator.clipboard
				.writeText( this.value );

			// Change icon for a two seconds
			this.$copyToClipboardButton.setAttribute( 'color', 'success' );
			this.$copyToClipboardButtonIcon.setAttribute( 'id', 'check' );

			setTimeout( () => {
				this.$copyToClipboardButton.setAttribute( 'color', 'primary' );
				this.$copyToClipboardButtonIcon.setAttribute( 'id', 'copy' );
			}, 2000 );
		} );
	}

	/**
	 * Listen changes to the input value
	 *
	 * @return {undefined}
	 */
	#listenInputChanges() {
		// Listen the change event on the inner field
		this.$input.addEventListener(
			'input',
			debounce( () => {
				// Trigger change event on the web component
				this.dispatchEvent(
					new CustomEvent( 'change', {
						detail: {
							context: 'user-input',
						},
					} )
				);
			}, 200 )
		);
	}

	/**
	 * Checks if we have to add the button to copy the field value to the clipboard
	 *
	 * @return {Boolean}
	 */
	 get copyToClipboard() {
		return this.hasAttribute( 'copy-to-clipboard' );
	}

	/**
	 * Sets whether we have to add the button to copy the field value to the clipboard
	 *
	 * @param {Boolean} value
	 * @return {undefined}
	 */
	set copyToClipboard( value ) {
		if ( value ) {
			// Set required status in the host
			this.setAttribute( 'copy-to-clipboard', '' );
		} else {
			// Set required status in the host
			this.removeAttribute( 'copy-to-clipboard' );
		}
	}

	/**
	 * Check if we should autofocus the field when it's added to the DOM
	 *
	 * @return {Boolean} True if it's autofocus
	 */
	get autoFocus() {
		return this._autofocus;
	}

	/**
	 * Set whether we should autofocus the field when it's added to the dOM
	 *
	 * @param {Boolean} value TRUE if the input should be autofocus
	 * @return {undefined}
	 */
	set autoFocus( value ) {
		this._autofocus = value;
	}

	/**
	 * Returns the value
	 *
	 * @return {String} The value
	 */
	get value() {
		return this.$input.value;
	}

	/**
	 * Sets the value
	 *
	 * @param {String} inputValue The value
	 * @return {undefined}
	 */
	set value( inputValue ) {
		if ( inputValue ) {
			// Set value in the host
			this.setAttribute( 'value', inputValue );

			// Creates a value in the element
			this.$input.value = inputValue;
		} else {
			// Set value in the host
			this.removeAttribute( 'value' );

			// Remove the value from the field
			this.$input.value = '';
		}

		// Dispatch change event
		this.dispatchEvent(
			new CustomEvent( 'change', {
				detail: {
					context: 'set-value',
				},
			} )
		);
	}

	/**
	 * Returns the placeholder
	 *
	 * @return {String} The placeholder
	 */
	get placeholder() {
		return this.getAttribute( 'placeholder' );
	}

	/**
	 * Sets the placeholder
	 *
	 * @param {String} inputPlaceholder The placeholder
	 * @return {undefined}
	 */
	set placeholder( inputPlaceholder ) {
		if ( inputPlaceholder ) {
			// Set placeholder in the host
			this.setAttribute( 'placeholder', inputPlaceholder );

			// Creates a placeholder in the element
			this.$input.setAttribute( 'placeholder', inputPlaceholder );
		} else {
			// Set placeholder in the host
			this.removeAttribute( 'placeholder' );

			// Remove the placeholder from the field
			this.$input.removeAttribute( 'placeholder' );
		}
	}

	/**
	 * Override (also defined in InputBase)
	 * Checks whether the input connected to the label is required
	 *
	 * @return {Boolean} True if it's required
	 */
	get required() {
		return this.hasAttribute( 'required' );
	}

	/**
	 * Override (also defined in InputBase)
	 * Sets whether the input connected to the label is required
	 *
	 * @param {Boolean} value TRUE if the input connected to the label is required
	 * @return {undefined}
	 */
	set required( value ) {
		if ( value ) {
			// Set required status in the host
			this.setAttribute( 'required', '' );

			// Set the attribute in the input
			this.$input.setAttribute( 'required', '' );
		} else {
			// Set required status in the host
			this.removeAttribute( 'required' );

			// Set the attribute in the input
			this.$input.removeAttribute( 'required' );
		}
	}

	/**
	 * Override (also defined in InputBase)
	 * Returns the id
	 *
	 * @return {String} The id
	 */
	get id() {
		return this.getAttribute( 'id' );
	}

	/**
	 * Override (also defined in InputBase)
	 * Sets the id
	 *
	 * @param {String} id The id
	 * @return {undefined}
	 */
	set id( value ) {
		if ( value ) {
			// Set id in the host
			this.setAttribute( 'id', value );
		} else {
			// Set id in the host
			this.removeAttribute( 'id' );
		}
	}

	/**
	 * Override (also defined in InputBase)
	 * Checks whether the input connected to the label is disabled
	 *
	 * @return {Boolean} True if it's disabled
	 */
	get disabled() {
		return this.hasAttribute( 'disabled' ) || this.$input.hasAttribute( 'disabled' );
	}

	/**
	 * Override (also defined in InputBase)
	 * Sets whether the input connected to the label is disabled
	 *
	 * @param {Boolean} value TRUE if the input connected to the label is disabled
	 * @return {undefined}
	 */
	set disabled( value ) {
		// Remove the disable attribute from the host
		// We will only add it to the real input
		this.removeAttribute( 'disabled' );

		if ( value ) {
			// Set the attribute in the input
			this.$fieldWrapper.classList.add( 'input-wrapper--disabled' );
			this.$input.setAttribute( 'disabled', '' );
		} else {
			// Set the attribute in the input
			this.$fieldWrapper.classList.remove( 'input-wrapper--disabled' );
			this.$input.removeAttribute( 'disabled' );
		}
	}

	/**
	 * Gets the button to copy to the clipboard
	 *
	 * @return {Node} 
	 */
	get $copyToClipboardButton() {
		return this.shadowRoot.querySelector( '.input-copy-clipboard' );
	}

	/**
	 * Gets the icon inside th button to copy to the clipboard
	 *
	 * @return {Node} 
	 */
	 get $copyToClipboardButtonIcon() {
		return this.$copyToClipboardButton.querySelector( 'uo-icon' );
	}

	/**
	 * Get the input wrapper
	 *
	 * @return {Node} The input wrapper
	 */
	get $fieldWrapper() {
		return this.shadowRoot.querySelector( '.input-wrapper' );
	}

	/**
	 * Get the input element
	 *
	 * @return {Node} The input element
	 */
	get $input() {
		return this.shadowRoot.querySelector( 'input' );
	}
}

customElements.define( 'uo-text-field', AutomatorInputText );
